import * as React from "react";

import TooltipOverlay, { type StateUpdate } from "./TooltipOverlay";

export type { TooltipTrigger } from "./TooltipOverlay";

/** Context API exposed to Tooltip: current visibility and overlay update callback. */
export type TooltipProviderState = {
    show: boolean;
    updateState: (ref: Element | null, container: Element | null, state: StateUpdate) => void;
};

const default_state: TooltipProviderState = {
    show: false,
    updateState: () => {},
};

/** React context connecting Tooltip triggers to the shared TooltipOverlay instance. */
export const TooltipContext = React.createContext<TooltipProviderState>(default_state);

type Props = {
    children: React.ReactNode;
};

/**
 * Mounts TooltipOverlay and provides TooltipContext to descendants.
 * Place once near the app root so all Tooltip wrappers share one overlay.
 *
 * TooltipOverlay is rendered before Provider children so it mounts first;
 * otherwise overlayRef is null during descendant componentDidMount/useLayoutEffect.
 */
class TooltipProvider extends React.Component<Props> {
    private overlayRef = React.createRef<TooltipOverlay>();
    private readonly contextValue: TooltipProviderState;

    constructor(props: Props) {
        super(props);
        const overlayRef = this.overlayRef;

        this.contextValue = {
            get show() {
                return overlayRef.current?.getShow() ?? false;
            },
            updateState: this.updateState,
        };
    }

    private updateState = (ref: Element | null, container: Element | null, state: StateUpdate) => {
        this.overlayRef.current?.updateState(ref, container, state);
    };

    render() {
        return (
            <>
                <TooltipOverlay ref={this.overlayRef} />
                <TooltipContext.Provider value={this.contextValue}>{this.props.children}</TooltipContext.Provider>
            </>
        );
    }
}

export default TooltipProvider;
